import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { PullDataService } from '../../service/PullDataService';
import { useAuthStore } from '../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import SearchableSelect from '../SearchableSelect';
import TagInput from '../Test/TagInput';

const LoadingPlaceholder = ({
  theme,
  label,
}: {
  theme?: string;
  label: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      py: '8px',
    }}
  >
    <CircularProgress size={16} sx={{ color: theme === 'dark' ? '#D0D5DD' : '#667085' }} />
    <Typography
      sx={{
        fontSize: '13px',
        color: theme === 'dark' ? '#98A2B3' : '#667085',
      }}
    >
      {label}
    </Typography>
  </Box>
);

interface MicrosoftFile {
  id: string;
  name: string;
}

interface MicrosoftWorksheet {
  id: string;
  name: string;
  position: number;
}

interface PullMicrosoftExcelDataProps {
  formData: any;
  onChange: (e: { target: { name: string; value: any } }) => void;
  onChangeSelect: (e: { target: { name: string; value: any } }) => void;
  data: any;
  nodeId: string;
  nodeLabel: string;
}

const PullMicrosoftExcelData: React.FC<PullMicrosoftExcelDataProps> = ({
  formData,
  onChange,
  onChangeSelect,
  data,
  nodeId,
  nodeLabel,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);

  const [excelFiles, setExcelFiles] = useState<MicrosoftFile[]>([]);
  const [worksheets, setWorksheets] = useState<MicrosoftWorksheet[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string>(
    data?.microsoftFileId ?? '',
  );
  const [selectedWorksheetId, setSelectedWorksheetId] = useState<string>(
    data?.microsoftWorksheetId ?? '',
  );
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingWorksheets, setLoadingWorksheets] = useState(false);

  const loadExcelFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const response = await PullDataService.getMicrosoftExcelFiles();
      setExcelFiles(response.data.files ?? []);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      setLoadingFiles(false);
    }
  }, [setNotification]);

  const loadWorksheets = useCallback(
    async (fileId: string) => {
      setLoadingWorksheets(true);
      try {
        const response = await PullDataService.getMicrosoftExcelWorksheets({
          fileId,
        });
        setWorksheets(response.data.worksheets ?? []);
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      } finally {
        setLoadingWorksheets(false);
      }
    },
    [setNotification],
  );

  useEffect(() => {
    loadExcelFiles();
  }, [loadExcelFiles]);

  useEffect(() => {
    if (selectedFileId) {
      loadWorksheets(selectedFileId);
    }
  }, [selectedFileId, loadWorksheets]);

  const fileOptions = excelFiles.map(f => ({
    label: f.name,
    value: f.id,
  }));

  const worksheetOptions = worksheets.map(w => ({
    label: w.name,
    value: w.id,
  }));

  const searchableSelectSx = {
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    '& .MuiSvgIcon-root': {
      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    },
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
  };

  const menuPropsSx = {
    PaperProps: {
      sx: {
        '& .Mui-selected': {
          backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
        },
        backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
      },
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        minWidth: '360px',
      }}
    >
      {loadingFiles ? (
        <LoadingPlaceholder theme={user?.theme} label="Loading files..." />
      ) : (
        <>
          <SearchableSelect
            options={fileOptions}
            label="Select an Excel file"
            onChange={value => {
              const fileId = value ? value.value : '';
              setSelectedFileId(fileId);
              setSelectedWorksheetId('');
              setWorksheets([]);
              setTimeout(() => {
                onChangeSelect({
                  target: { name: 'microsoftFileId', value: fileId },
                });
              }, 0);
            }}
            value={fileOptions.find(o => o.value === selectedFileId) || null}
            className="nodrag"
            style={{ width: 100 }}
            sx={searchableSelectSx}
            MenuProps={menuPropsSx}
          />

          {loadingWorksheets ? (
            <LoadingPlaceholder theme={user?.theme} label="Loading worksheets..." />
          ) : (
            <>
              <SearchableSelect
                options={worksheetOptions}
                label="Select a worksheet"
                onChange={value => {
                  const worksheetId = value ? value.value : '';
                  setSelectedWorksheetId(worksheetId);
                  setTimeout(() => {
                    onChangeSelect({
                      target: { name: 'microsoftWorksheetId', value: worksheetId },
                    });
                  }, 0);
                }}
                value={
                  worksheetOptions.find(o => o.value === selectedWorksheetId) ||
                  null
                }
                style={{ width: 200 }}
                sx={{
                  ...searchableSelectSx,
                  bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                }}
                MenuProps={menuPropsSx}
              />

              <TagInput
                placeholder="Use A1 Notation. Example: A1:Z40"
                onChange={onChange}
                name="rangeFilter"
                value={formData.rangeFilter ?? ''}
                isAdvanced
                showCopy
                dataConnected={data?.rangeFilterData || []}
                removeIcons
                labelName={'Select Area (Optional)'}
                isCollapsed
                nodeId={nodeId}
                handleId="rangeFilter"
                nodeLabel={nodeLabel}
                labelStyle={{
                  fontSize: '14px',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  fontWeight: 400,
                }}
                advancedCollapsed
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default PullMicrosoftExcelData;
